import { useTranslation, useEnvironment, Trans } from '@wix/yoshi-flow-editor';
import React, { useState, useEffect } from 'react';
import { isRescheduleAble } from '../../../../../utils/bookInfo';
import {
  ActionButtonUiTypes,
  BookingsGroup,
  CloseModalTriggers,
  RescheduleButtonOrigin,
} from '../../../../../types';
import { CancellationModalDataHooks } from '../../../../../components/MyBookings/Widget/datahooks';
import { Modal } from '../../../../../components/MyBookings/Components/Modal';
import { useApplicationContext } from '../../../../hooks/useApplicationContext';
import Link from '../../../../../components/MyBookings/Components/Link';
import { Reschedule } from '../../../../../components/MyBookings/Widget/ActionsButtons/Reschedule';
import { classes } from '../CancellationModal.st.css';
import { BookingPolicyModalContent } from './PolicyModal';

export interface CancellationFeeModalContentProps {
  group: BookingsGroup;
}

export const CancellationFeeModalContent: React.FC<
  CancellationFeeModalContentProps
> = ({ group }) => {
  const [isBookingPolicyModalOpen, setIsBookingPolicyModalOpen] =
    useState<boolean>(false);
  const { actions, groupTotalFees } = useApplicationContext();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const groupFeeId = group.groupId || group.bookings[0]?.bookingId;
  const totalFeeToDisplay = groupTotalFees?.[groupFeeId];

  useEffect(() => {
    if (!totalFeeToDisplay) {
      actions.getListBookingFees({
        group,
      });
    }
  }, []);

  const handleCancelButtonClick = () => {
    actions.clickOnCancel({
      group,
    });
    actions.closeModal();
  };

  const handleCloseButtonClick = () => {
    actions.closeModal(CloseModalTriggers.CloseModal);
  };

  const onBookingPolicyOpen = () => {
    setIsBookingPolicyModalOpen(true);
  };

  const onBookingPolicyClose = () => {
    setIsBookingPolicyModalOpen(false);
  };

  const renderCancellationFeeModalContent = () => (
    <Modal
      dataHook={CancellationModalDataHooks.CancellationFee}
      isLoading={!totalFeeToDisplay}
      modalTitle={t('app.my-bookings-widget.cancellation-fee-modal.title')}
      modalSubtitle={
        <Trans
          i18nKey="app.my-bookings-widget.cancellation-fee-modal.content"
          values={{
            amount: totalFeeToDisplay?.amount?.formattedValue,
          }}
        >
          <Link
            data-hook={
              CancellationModalDataHooks.ShowBookingPolicyLinkFromCancelFee
            }
            onClick={onBookingPolicyOpen}
          />
        </Trans>
      }
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-fee-modal.primary-button.label',
          ),
          onClick: handleCancelButtonClick,
        },
      }}
      secondaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-fee-modal.secondary-button.label',
          ),
          onClick: handleCloseButtonClick,
        },
      }}
    />
  );

  const renderCancellationFeeModalWithRescheduleContent = () => (
    <Modal
      dataHook={CancellationModalDataHooks.CancellationFeeWithReschedule}
      isLoading={!totalFeeToDisplay}
      modalTitle={t(
        'app.my-bookings-widget.cancellation-fee-reschedule-modal.title',
      )}
      modalSubtitle={
        <Trans
          i18nKey="app.my-bookings-widget.cancellation-fee-reschedule-modal.content"
          values={{
            amount: totalFeeToDisplay?.amount?.formattedValue,
          }}
        >
          <Link
            data-hook={
              CancellationModalDataHooks.ShowBookingPolicyLinkFromRescheduleCancelFee
            }
            onClick={onBookingPolicyOpen}
          />
        </Trans>
      }
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-fee-reschedule-modal.primary-button.label',
          ),
          onClick: handleCancelButtonClick,
        },
      }}
      secondaryButton={{
        component: (
          <Reschedule
            origin={RescheduleButtonOrigin.CancellationFeeModal}
            label={t(
              'app.my-bookings-widget.cancellation-fee-reschedule-modal.secondary-button.label',
            )}
            group={group}
            fullWidthButton={isMobile}
            type={ActionButtonUiTypes.BUTTON}
            className={classes.rescheduleButton}
          />
        ),
      }}
    />
  );

  const renderModalContent = () => {
    if (isBookingPolicyModalOpen) {
      return (
        <BookingPolicyModalContent
          group={group}
          onClose={onBookingPolicyClose}
        />
      );
    } else if (isRescheduleAble(group)) {
      return renderCancellationFeeModalWithRescheduleContent();
    } else {
      return renderCancellationFeeModalContent();
    }
  };

  return renderModalContent();
};
